import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { can } from "@sts/schemas";
import {
  PIPE_DIAMETERS_MM,
  PIPE_MATERIAL_NAMES,
  PLAN_PIPE_MATERIALS,
  type PlanPipeMaterial,
  STOCK_GROUPS,
  STOCK_GROUP_TITLES,
  STOCK_RULE_KINDS,
  type PipeDiameterMm,
  type StockGroup,
  type StockLine,
  type StockLineAdjustment,
  type StockRuleKind,
  type StockSettings,
} from "@sts/rules-data";
import type {
  ListedDocument,
  Me,
  StockAdjustmentsPatch,
  StockCatalogueItem,
  StockManualLineInput,
  SurveyStock as SurveyStockData,
} from "../api/types.js";
import { Async, Failure } from "../components/Async.jsx";
import { usePortal } from "../app/context.js";
import { href } from "../app/router.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { longDate } from "../lib/format.js";
import { supersededLine, versionWord } from "../lib/documents.js";

/**
 * Stock and the installer pack, for one survey.
 *
 * Josh, 2026-09-10: "once the design is complete from our side … we should be
 * able to have all of the stock report … Then at last, there should be one
 * installer pack … and we should be able to download the PDF file."
 *
 * The report itself is computed by the API from the plan objects, the design
 * and the catalogue (`@sts/rules-data` `computeStockReport`); every write here
 * answers with the recomputed report, so the screen never shows a figure the
 * server would not. What a person changes is only ever an ADJUSTMENT — an
 * override that keeps the derived figure visible beside it, an exclusion, a
 * manual line, or one of the allowances — and each carries its basis.
 *
 * PDFs and the preview are fetched through the authenticated client (a plain
 * `<a href>` cannot send the Bearer header), the way `photoImageObjectUrl`
 * fetches photographs. The preview is shown in a sandboxed iframe: no
 * scripts, an opaque origin.
 */

const RULE_LABELS: Readonly<Record<StockRuleKind, string>> = {
  "per-job": "per job",
  "per-radiator": "per radiator",
  "per-heat-pump": "per heat pump",
  "per-metre-of-pipe": "per metre of pipe",
  "per-100-litres-of-system-water": "per 100 L of system water",
};

const SOURCE_LABELS: Readonly<Record<string, string>> = {
  plan: "plan",
  design: "design",
  R: "R",
  V: "V",
  I: "I",
  RoT: "RoT",
  assumption: "assumed",
  catalogue: "catalogue",
  manual: "manual",
};

const SOURCE_TITLES: Readonly<Record<string, string>> = {
  plan: "Counted off the floor plan",
  design: "From the design decisions",
  R: "A rule read in a standard or manufacturer's document",
  V: "A vendor's or distributor's figure",
  I: "Inferred from the drawing",
  RoT: "Rule of thumb, not in any standard",
  assumption: "A default Scannix picked — check it",
  catalogue: "Your organisation's stock catalogue rule",
  manual: "Added by hand for this job",
};

function qty(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function SourceChip({ source }: { readonly source: string }): ReactNode {
  return (
    <span className="stock-source" title={SOURCE_TITLES[source] ?? source}>
      {SOURCE_LABELS[source] ?? source}
    </span>
  );
}

export function SurveyStock({ surveyId }: { readonly surveyId: string }): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync(async () => {
    const [stock, me] = await Promise.all([client.surveyStock(surveyId), client.me()]);
    return { stock, me };
  }, [client, surveyId]);
  return (
    <Async state={state} what="the stock report" reload={reload}>
      {(page) => <Workspace initial={page.stock} me={page.me} surveyId={surveyId} />}
    </Async>
  );
}

function Workspace({
  initial,
  me,
  surveyId,
}: {
  readonly initial: SurveyStockData;
  readonly me: Me;
  readonly surveyId: string;
}): ReactNode {
  const { client } = usePortal();
  const [stock, setStock] = useState(initial);
  const { busy, error, submit } = useSubmit();

  const save = useCallback(
    (patch: StockAdjustmentsPatch) =>
      submit(async () => {
        setStock(await client.patchStockAdjustments(surveyId, patch));
      }),
    [client, submit, surveyId],
  );
  const refresh = useCallback(async () => {
    setStock(await client.surveyStock(surveyId));
  }, [client, surveyId]);

  const report = stock.report;
  return (
    <>
      <div className="page-header">
        <p className="small">
          <a href={href.survey(surveyId)}>Back to the survey</a>
        </p>
        <h1>Stock &amp; installer pack</h1>
        <p className="lede">
          {report.counts.heatPumps} heat pump{report.counts.heatPumps === 1 ? "" : "s"}, {report.counts.radiators}{" "}
          radiator{report.counts.radiators === 1 ? "" : "s"} and {report.counts.pipeRuns} pipe run
          {report.counts.pipeRuns === 1 ? "" : "s"} on the plan. Quantities are worked out from the drawing; every
          one says how, and any can be overridden.
        </p>
      </div>

      <PackActions
        surveyId={surveyId}
        jobId={stock.jobId}
        mayIssue={can({ role: me.role, resource: "document", action: "issue" })}
      />

      {report.warnings.length > 0 ? (
        <div className="notice warn" role="status">
          <p>
            <strong>Check before loading the van</strong>
          </p>
          <ul>
            {report.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {error !== null ? <Failure error={error} what="that change" /> : null}

      <Allowances stock={stock} busy={busy} onSave={save} />
      <SystemWater stock={stock} />
      <StockTable stock={stock} busy={busy} onSave={save} />
      <ManualLines stock={stock} busy={busy} onSave={save} />
      <Catalogue items={stock.catalogue} me={me} onChanged={refresh} />
    </>
  );
}

// ------------------------------------------------------------ the pack --

function PackActions({
  surveyId,
  jobId,
  mayIssue,
}: {
  readonly surveyId: string;
  readonly jobId: string;
  /**
   * Holds `document: issue`, as the API's issue route enforces. Issuing is the
   * one act here that files a document; a role without it (an installer on
   * the job who opened this screen by its address) is not offered it.
   */
  readonly mayIssue: boolean;
}): ReactNode {
  const { client } = usePortal();
  const [preview, setPreview] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [issued, setIssued] = useState<readonly ListedDocument[] | null>(null);
  const { busy, error, submit } = useSubmit();

  const loadIssued = useCallback(async () => {
    try {
      const docs = await client.jobDocuments(jobId);
      setIssued(docs.filter((d) => d.type === "installer-pack"));
    } catch {
      // A role that cannot list the job's documents still gets the draft.
      setIssued(null);
    }
  }, [client, jobId]);

  useEffect(() => {
    void loadIssued();
  }, [loadIssued]);
  // The pack an issue would supersede: the newest issued one nothing replaces.
  const currentPack = issued?.find((d) => d.status === "issued") ?? null;

  const download = (fetchFile: () => Promise<{ url: string; filename: string }>) =>
    submit(async () => {
      const file = await fetchFile();
      const a = document.createElement("a");
      a.href = file.url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Long enough for the browser to take the bytes; then let the blob go.
      setTimeout(() => URL.revokeObjectURL(file.url), 60_000);
    });

  return (
    <section aria-labelledby="pack-heading">
      <h2 id="pack-heading">Installer pack</h2>
      <p className="small muted">
        Job card, design brief (MIS 3005-D 3.8.1), floor plans, radiator and pipe schedules, the pick list below,
        commissioning checks and sign-off — with blanks for the team to fill in on site. A draft is marked DRAFT on
        every page; issuing stores a fixed, hashed copy.
      </p>
      <div className="stock-actions">
        <button
          type="button"
          className="secondary"
          disabled={busy}
          onClick={() =>
            submit(async () => {
              setPreview(preview === null ? await client.installerPackHtml(surveyId) : null);
            })
          }
        >
          {preview === null ? "Preview installer pack" : "Close preview"}
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => download(() => client.installerPackPdf(surveyId))}>
          Download PDF
        </button>
        {!mayIssue ? null : confirming ? (
          <>
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() =>
                submit(async () => {
                  await client.issueInstallerPack(surveyId);
                  setConfirming(false);
                  await loadIssued();
                })
              }
            >
              Issue now — store a fixed copy
            </button>
            <button type="button" className="link" onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </>
        ) : (
          <button type="button" className="secondary" disabled={busy} onClick={() => setConfirming(true)}>
            Issue installer pack
          </button>
        )}
      </div>
      {confirming && currentPack ? (
        <p className="small" role="note" data-testid="pack-supersedes">
          Issuing now supersedes the current pack
          {versionWord(currentPack) ? ` (${versionWord(currentPack)}, ${longDate(currentPack.createdAt)})` : ""}. It
          stays stored and printable, marked superseded; the new one becomes the pack the team builds from.
        </p>
      ) : null}
      {error !== null ? <Failure error={error} what="the installer pack" /> : null}
      {issued !== null && issued.length > 0 ? (
        <ul className="stock-issued" aria-label="Issued installer packs">
          {issued.map((d) => (
            <li key={d.id} data-document-id={d.id} data-status={d.status}>
              <span>
                {d.title}
                {versionWord(d) ? ` · ${versionWord(d)}` : ""} · {longDate(d.createdAt)}
                {d.status === "superseded" ? (
                  <>
                    {" · "}
                    <strong>{supersededLine(d, issued, longDate) ?? "Superseded."}</strong>
                  </>
                ) : d.status === "issued" && issued.some((o) => o.status === "superseded") ? (
                  <>
                    {" · "}
                    <strong>Current</strong>
                  </>
                ) : null}
              </span>
              <button
                type="button"
                className="link"
                disabled={busy}
                onClick={() => download(() => client.issuedInstallerPackPdf(surveyId, d.id))}
              >
                Download issued PDF
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {preview !== null ? (
        <iframe className="stock-preview" title="Installer pack preview" sandbox="" srcDoc={preview} />
      ) : null}
    </section>
  );
}

// ------------------------------------------------------- allowances --

interface AllowanceForm {
  dropPerRadiatorM: string;
  dropDiameterMm: string;
  dropMaterial: PlanPipeMaterial;
  wastagePct: string;
  stockLengthM: string;
  coilLengthM: string;
  insulationCoveragePct: string;
  glycolConcentrationPct: string;
  extraPipe: { diameterMm: string; metres: string; material: PlanPipeMaterial }[];
  volumeExtras: { label: string; litres: string }[];
}

type NumberSetting =
  | "dropPerRadiatorM"
  | "wastagePct"
  | "stockLengthM"
  | "coilLengthM"
  | "insulationCoveragePct"
  | "glycolConcentrationPct";

function formFrom(settings: StockSettings): AllowanceForm {
  return {
    dropPerRadiatorM: String(settings.dropPerRadiatorM),
    dropDiameterMm: String(settings.dropDiameterMm),
    dropMaterial: settings.dropMaterial,
    wastagePct: String(settings.wastagePct),
    stockLengthM: String(settings.stockLengthM),
    coilLengthM: String(settings.coilLengthM),
    insulationCoveragePct: String(settings.insulationCoveragePct),
    glycolConcentrationPct: String(settings.glycolConcentrationPct),
    extraPipe: settings.extraPipe.map((e) => ({
      diameterMm: String(e.diameterMm),
      metres: String(e.metres),
      material: e.material ?? "copper",
    })),
    volumeExtras: settings.volumeExtras.map((v) => ({ label: v.label, litres: String(v.litres) })),
  };
}

function Allowances({
  stock,
  busy,
  onSave,
}: {
  readonly stock: SurveyStockData;
  readonly busy: boolean;
  readonly onSave: (patch: StockAdjustmentsPatch) => Promise<void>;
}): ReactNode {
  const [form, setForm] = useState(() => formFrom(stock.report.settings));
  useEffect(() => setForm(formFrom(stock.report.settings)), [stock.report.settings]);
  const d = stock.defaults;
  const basis = stock.settingBasis;

  const numberOrDefault = (key: NumberSetting): number | null => {
    const value = Number(form[key]);
    return value === d[key] ? null : value;
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const drop = Number(form.dropDiameterMm) as PipeDiameterMm;
    const extra = form.extraPipe
      .filter((e) => e.metres.trim() !== "")
      .map((e) => ({
        diameterMm: Number(e.diameterMm) as PipeDiameterMm,
        metres: Number(e.metres),
        ...(e.material === "copper" ? {} : { material: e.material }),
      }));
    const water = form.volumeExtras
      .filter((v) => v.label.trim() !== "" && v.litres.trim() !== "")
      .map((v) => ({ label: v.label.trim(), litres: Number(v.litres) }));
    void onSave({
      settings: {
        dropPerRadiatorM: numberOrDefault("dropPerRadiatorM"),
        dropDiameterMm: drop === d.dropDiameterMm ? null : drop,
        dropMaterial: form.dropMaterial === d.dropMaterial ? null : form.dropMaterial,
        wastagePct: numberOrDefault("wastagePct"),
        stockLengthM: numberOrDefault("stockLengthM"),
        coilLengthM: numberOrDefault("coilLengthM"),
        insulationCoveragePct: numberOrDefault("insulationCoveragePct"),
        glycolConcentrationPct: numberOrDefault("glycolConcentrationPct"),
        extraPipe: extra.length === 0 ? null : extra,
        volumeExtras: water.length === 0 ? null : water,
      },
    });
  };

  const numberField = (key: NumberSetting, label: string, unit: string, step: string) => (
    <div className="field">
      <label htmlFor={`allow-${key}`}>
        {label} ({unit})
      </label>
      <input
        id={`allow-${key}`}
        type="number"
        min="0"
        step={step}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
      <span className="small muted">
        Default {d[key]} {unit} <SourceChip source={basis[key].source} /> {basis[key].text}
      </span>
    </div>
  );

  const materialSelect = (id: string, label: string, value: PlanPipeMaterial, set: (m: PlanPipeMaterial) => void) => (
    <select id={id} aria-label={label} value={value} onChange={(e) => set(e.target.value as PlanPipeMaterial)}>
      {PLAN_PIPE_MATERIALS.map((m) => (
        <option key={m} value={m}>
          {PIPE_MATERIAL_NAMES[m]}
        </option>
      ))}
    </select>
  );

  return (
    <section aria-labelledby="allow-heading">
      <h2 id="allow-heading">Allowances</h2>
      <p className="small muted">
        The plan is drawn from above, so it has no vertical pipe. These add what it cannot show, and turn metres into
        what is bought.
      </p>
      <form className="stock-allowances" onSubmit={onSubmit}>
        {numberField("dropPerRadiatorM", "Drop allowance per radiator", "m", "0.1")}
        <div className="field">
          <label htmlFor="allow-dropDiameterMm">Drops counted in (mm)</label>
          <select
            id="allow-dropDiameterMm"
            value={form.dropDiameterMm}
            onChange={(e) => setForm({ ...form, dropDiameterMm: e.target.value })}
          >
            {PIPE_DIAMETERS_MM.map((mm) => (
              <option key={mm} value={mm}>
                {mm} mm
              </option>
            ))}
          </select>
          <span className="small muted">
            Default {d.dropDiameterMm} mm <SourceChip source={basis.dropDiameterMm.source} /> {basis.dropDiameterMm.text}
          </span>
        </div>
        <div className="field">
          <label htmlFor="allow-dropMaterial">Drops made of</label>
          {materialSelect("allow-dropMaterial", "Drops made of", form.dropMaterial, (m) => setForm({ ...form, dropMaterial: m }))}
          <span className="small muted">
            Default {PIPE_MATERIAL_NAMES[d.dropMaterial]} <SourceChip source={basis.dropMaterial.source} />{" "}
            {basis.dropMaterial.text}
          </span>
        </div>
        {numberField("wastagePct", "Wastage", "%", "1")}
        {numberField("stockLengthM", "Pipe stock length", "m", "0.5")}
        {numberField("coilLengthM", "Plastic pipe coil (0 = listed sizes)", "m", "5")}
        {numberField("insulationCoveragePct", "Insulation coverage", "%", "5")}
        {numberField("glycolConcentrationPct", "Glycol (0 = none)", "%", "1")}
        <fieldset className="stock-extra">
          <legend>Extra pipe — risers, the run to the outdoor unit</legend>
          <span className="small muted">
            <SourceChip source={basis.extraPipe.source} /> {basis.extraPipe.text}
          </span>
          {form.extraPipe.map((entry, i) => (
            <div className="stock-extra__row" key={i}>
              <select
                aria-label={`Extra pipe ${i + 1} size`}
                value={entry.diameterMm}
                onChange={(e) =>
                  setForm({
                    ...form,
                    extraPipe: form.extraPipe.map((x, j) => (j === i ? { ...x, diameterMm: e.target.value } : x)),
                  })
                }
              >
                {PIPE_DIAMETERS_MM.map((mm) => (
                  <option key={mm} value={mm}>
                    {mm} mm
                  </option>
                ))}
              </select>
              {materialSelect(`extra-material-${i}`, `Extra pipe ${i + 1} material`, entry.material, (m) =>
                setForm({ ...form, extraPipe: form.extraPipe.map((x, j) => (j === i ? { ...x, material: m } : x)) }),
              )}
              <input
                aria-label={`Extra pipe ${i + 1} metres`}
                type="number"
                min="0"
                step="0.1"
                value={entry.metres}
                onChange={(e) =>
                  setForm({
                    ...form,
                    extraPipe: form.extraPipe.map((x, j) => (j === i ? { ...x, metres: e.target.value } : x)),
                  })
                }
              />
              <span>m</span>
              <button
                type="button"
                className="link"
                onClick={() => setForm({ ...form, extraPipe: form.extraPipe.filter((_, j) => j !== i) })}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className="link"
            onClick={() =>
              setForm({ ...form, extraPipe: [...form.extraPipe, { diameterMm: "22", metres: "", material: "copper" }] })
            }
          >
            Add extra pipe
          </button>
        </fieldset>
        <fieldset className="stock-extra">
          <legend>System water the plan cannot count</legend>
          <span className="small muted">
            <SourceChip source={basis.volumeExtras.source} /> {basis.volumeExtras.text}
          </span>
          {form.volumeExtras.map((entry, i) => (
            <div className="stock-extra__row" key={i}>
              <input
                aria-label={`System water ${i + 1} — what`}
                placeholder="Heat pump (datasheet)"
                maxLength={120}
                value={entry.label}
                onChange={(e) =>
                  setForm({
                    ...form,
                    volumeExtras: form.volumeExtras.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)),
                  })
                }
              />
              <input
                aria-label={`System water ${i + 1} litres`}
                type="number"
                min="0"
                step="0.1"
                value={entry.litres}
                onChange={(e) =>
                  setForm({
                    ...form,
                    volumeExtras: form.volumeExtras.map((x, j) => (j === i ? { ...x, litres: e.target.value } : x)),
                  })
                }
              />
              <span>L</span>
              <button
                type="button"
                className="link"
                onClick={() => setForm({ ...form, volumeExtras: form.volumeExtras.filter((_, j) => j !== i) })}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className="link"
            onClick={() => setForm({ ...form, volumeExtras: [...form.volumeExtras, { label: "", litres: "" }] })}
          >
            Add water
          </button>
        </fieldset>
        <div>
          <button type="submit" className="primary" disabled={busy}>
            Save allowances
          </button>
        </div>
      </form>
    </section>
  );
}

// ------------------------------------------------------- system water --

/**
 * The system water volume, part by part, and what it leaves out — the figure
 * inhibitor, cleaner and glycol are dosed from. Read-only: the parts come from
 * the plan and the allowances above.
 */
function SystemWater({ stock }: { readonly stock: SurveyStockData }): ReactNode {
  const volume = stock.report.systemVolume;
  const glycol = stock.report.glycol;
  return (
    <section aria-labelledby="water-heading" data-testid="system-water">
      <h2 id="water-heading">System water</h2>
      {volume.lines.length === 0 ? (
        <p className="small muted">Nothing on the plan holds water that can be counted yet.</p>
      ) : (
        <div className="table-scroll">
          <table aria-label="System water volume">
            <thead>
              <tr>
                <th>Part</th>
                <th className="n">Litres</th>
                <th>Basis</th>
              </tr>
            </thead>
            <tbody>
              {volume.lines.map((l) => (
                <tr key={l.key}>
                  <td>{l.component}</td>
                  <td className="n">{l.litres === null ? <strong>not included</strong> : l.litres.toFixed(1)}</td>
                  <td className="small muted">
                    <SourceChip source={l.source} /> {l.basis}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>{volume.notIncluded.length > 0 ? "Counted so far (a minimum)" : "Total"}</th>
                <td className="n">
                  <strong>{volume.knownLitres.toFixed(1)}</strong>
                </td>
                <td className="small">
                  {volume.notIncluded.length > 0
                    ? `Not included: ${volume.notIncluded.join("; ")}. Add them under "System water the plan cannot count".`
                    : "Every part on the job is counted."}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <p className="small muted">
        {glycol
          ? `Glycol: ${glycol.concentrationPct}% of ${volume.knownLitres.toFixed(1)} L = ${glycol.litres} L (see Water treatment below).`
          : "No glycol set: frost protection by anti-freeze valves."}{" "}
        Dose inhibitor and cleaner from this volume by giving them a &ldquo;per 100 L of system water&rdquo; rule in the
        catalogue, from the product&apos;s label.
      </p>
    </section>
  );
}

// ------------------------------------------------------------- lines --

function StockTable({
  stock,
  busy,
  onSave,
}: {
  readonly stock: SurveyStockData;
  readonly busy: boolean;
  readonly onSave: (patch: StockAdjustmentsPatch) => Promise<void>;
}): ReactNode {
  const adjustments = stock.adjustments.lines;

  /** A line's whole adjustment, with one change applied — or null when nothing is left. */
  const withChange = (key: string, change: Partial<StockLineAdjustment>): StockLineAdjustment | null => {
    const merged: { quantity?: number; excluded?: boolean; note?: string } = { ...adjustments[key], ...change };
    if (merged.quantity === undefined) delete merged.quantity;
    if (!merged.excluded) delete merged.excluded;
    if (!merged.note) delete merged.note;
    return Object.keys(merged).length === 0 ? null : merged;
  };

  const setQuantity = (line: StockLine, raw: string) => {
    if (raw.trim() === "") return;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0 || value === line.quantity) return;
    // Typing the derived figure back in is the same as clearing the override.
    const next = line.derivedQuantity !== null && value === line.derivedQuantity ? { quantity: undefined } : { quantity: value };
    if (line.origin === "manual") {
      void onSave({
        manualLines: stock.adjustments.manualLines.map((m) =>
          `manual:${m.id}` === line.key ? { ...m, quantity: value } : m,
        ),
      });
      return;
    }
    void onSave({ lines: { [line.key]: withChange(line.key, next) } });
  };

  return (
    <section aria-labelledby="stock-heading">
      <h2 id="stock-heading">Stock</h2>
      {stock.report.groups.map((group) => (
        <div key={group.group} className="stock-group">
          <h3>{group.title}</h3>
          {group.lines.length > 0 ? (
            <div className="table-scroll">
              <table aria-label={group.title}>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="n">Derived</th>
                    <th className="n">Quantity</th>
                    <th>Unit</th>
                    <th>Leave out</th>
                  </tr>
                </thead>
                <tbody>
                  {group.lines.map((line) => (
                    <tr key={line.key} className={line.excluded ? "stock-line--excluded" : undefined}>
                      <td>
                        <strong>{line.item}</strong>
                        {line.spec ? <div>{line.spec}</div> : null}
                        {line.measure ? <div className="small">{line.measure}</div> : null}
                        <div className="small muted stock-basis">
                          <SourceChip source={line.source} /> {line.basis}
                        </div>
                        {line.note ? <div className="small">Note: {line.note}</div> : null}
                      </td>
                      <td className="n">{line.derivedQuantity === null ? "—" : qty(line.derivedQuantity)}</td>
                      <td className="n">
                        <input
                          key={`${line.key}:${line.quantity}`}
                          className="stock-qty"
                          type="number"
                          min="0"
                          step="any"
                          aria-label={`Quantity: ${line.item}${line.spec ? ` (${line.spec})` : ""}`}
                          defaultValue={line.quantity}
                          disabled={busy}
                          onBlur={(e) => setQuantity(line, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") setQuantity(line, (e.target as HTMLInputElement).value);
                          }}
                        />
                        {line.overridden ? (
                          <button
                            type="button"
                            className="link"
                            disabled={busy}
                            onClick={() => void onSave({ lines: { [line.key]: withChange(line.key, { quantity: undefined }) } })}
                          >
                            Use derived
                          </button>
                        ) : null}
                      </td>
                      <td>{line.unit}</td>
                      <td>
                        {line.origin === "manual" ? (
                          <button
                            type="button"
                            className="link"
                            disabled={busy}
                            onClick={() =>
                              void onSave({
                                manualLines: stock.adjustments.manualLines.filter((m) => `manual:${m.id}` !== line.key),
                              })
                            }
                          >
                            Remove
                          </button>
                        ) : (
                          <input
                            type="checkbox"
                            aria-label={`Leave out: ${line.item}${line.spec ? ` (${line.spec})` : ""}`}
                            checked={line.excluded}
                            disabled={busy}
                            onChange={(e) =>
                              void onSave({ lines: { [line.key]: withChange(line.key, { excluded: e.target.checked }) } })
                            }
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {group.notes.map((n) => (
            <p key={n} className="small muted">
              {n}
            </p>
          ))}
        </div>
      ))}
      {stock.report.unmatchedAdjustmentKeys.length > 0 ? (
        <div className="notice pending">
          <p>
            {stock.report.unmatchedAdjustmentKeys.length} saved adjustment
            {stock.report.unmatchedAdjustmentKeys.length === 1 ? " belongs" : "s belong"} to lines no longer on the plan.
          </p>
          <button
            type="button"
            className="link"
            disabled={busy}
            onClick={() =>
              void onSave({ lines: Object.fromEntries(stock.report.unmatchedAdjustmentKeys.map((k) => [k, null])) })
            }
          >
            Clear them
          </button>
        </div>
      ) : null}
    </section>
  );
}

// ------------------------------------------------------ manual lines --

const EMPTY_MANUAL = { group: "other" as StockGroup, item: "", spec: "", quantity: "1", unit: "ea", note: "" };

function ManualLines({
  stock,
  busy,
  onSave,
}: {
  readonly stock: SurveyStockData;
  readonly busy: boolean;
  readonly onSave: (patch: StockAdjustmentsPatch) => Promise<void>;
}): ReactNode {
  const [draft, setDraft] = useState(EMPTY_MANUAL);
  const fromCatalogue = (id: string) => {
    const item = stock.catalogue.find((c) => c.id === id);
    if (!item) return;
    setDraft({ ...draft, group: item.category, item: item.name, unit: item.unit, spec: item.code ? `Code ${item.code}` : "" });
  };
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const line: StockManualLineInput = {
      group: draft.group,
      item: draft.item.trim(),
      spec: draft.spec.trim(),
      quantity: Number(draft.quantity),
      unit: draft.unit.trim(),
      note: draft.note.trim(),
    };
    void onSave({ manualLines: [...stock.adjustments.manualLines, line] }).then(() => setDraft(EMPTY_MANUAL));
  };
  return (
    <section aria-labelledby="manual-heading">
      <h2 id="manual-heading">Add a line for this job</h2>
      <form className="stacked" onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="manual-catalogue">From the catalogue</label>
          <select id="manual-catalogue" value="" onChange={(e) => fromCatalogue(e.target.value)}>
            <option value="">Choose…</option>
            {stock.catalogue
              .filter((c) => c.active)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="manual-group">Group</label>
          <select id="manual-group" value={draft.group} onChange={(e) => setDraft({ ...draft, group: e.target.value as StockGroup })}>
            {STOCK_GROUPS.map((g) => (
              <option key={g} value={g}>
                {STOCK_GROUP_TITLES[g]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="manual-item">Item</label>
          <input id="manual-item" required value={draft.item} onChange={(e) => setDraft({ ...draft, item: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="manual-spec">Spec</label>
          <input id="manual-spec" value={draft.spec} onChange={(e) => setDraft({ ...draft, spec: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="manual-quantity">Quantity</label>
          <input
            id="manual-quantity"
            type="number"
            min="0"
            step="any"
            required
            value={draft.quantity}
            onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="manual-unit">Unit</label>
          <input id="manual-unit" required value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="manual-note">Why</label>
          <input id="manual-note" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
        </div>
        <button type="submit" className="primary" disabled={busy}>
          Add line
        </button>
      </form>
    </section>
  );
}

// --------------------------------------------------------- catalogue --

const EMPTY_ITEM = { name: "", unit: "ea", category: "plant" as StockGroup, code: "", ruleKind: "", ruleQuantity: "1" };

function Catalogue({
  items,
  me,
  onChanged,
}: {
  readonly items: readonly StockCatalogueItem[];
  readonly me: Me;
  readonly onChanged: () => Promise<void>;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [draft, setDraft] = useState(EMPTY_ITEM);
  // Changing the catalogue is `org-settings: update` — owner and admin.
  const canEdit = me.role === "owner" || me.role === "admin";

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void submit(async () => {
      await client.createStockCatalogueItem({
        name: draft.name.trim(),
        unit: draft.unit.trim(),
        category: draft.category,
        code: draft.code.trim() || null,
        rule: draft.ruleKind ? { kind: draft.ruleKind as StockRuleKind, quantity: Number(draft.ruleQuantity) } : null,
      });
      setDraft(EMPTY_ITEM);
      await onChanged();
    });
  };

  return (
    <section aria-labelledby="catalogue-heading">
      <details>
        <summary>
          <h2 id="catalogue-heading">Organisation stock catalogue ({items.length})</h2>
        </summary>
        <p className="small muted">
          Your organisation's standing stock list. Items with a rule are added to every job's stock automatically;
          items without one are offered under "Add a line for this job". The defaults were copied in from the
          research note and each says where its rule came from. No prices are kept here.
          {canEdit ? "" : " Only an owner or admin can change this list."}
        </p>
        {error !== null ? <Failure error={error} what="that catalogue change" /> : null}
        <div className="table-scroll">
          <table aria-label="Stock catalogue">
            <thead>
              <tr>
                <th>Item</th>
                <th>Group</th>
                <th>Unit</th>
                <th>Code</th>
                <th>Default quantity</th>
                <th>In use</th>
                {canEdit ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.name}</strong>
                    {item.basis ? (
                      <div className="small muted">
                        {item.source ? <SourceChip source={item.source} /> : null} {item.basis}
                      </div>
                    ) : null}
                  </td>
                  <td>{STOCK_GROUP_TITLES[item.category]}</td>
                  <td>{item.unit}</td>
                  <td>{item.code ?? ""}</td>
                  <td>{item.rule ? `${qty(item.rule.quantity)} ${RULE_LABELS[item.rule.kind]}` : "Add by hand"}</td>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`In use: ${item.name}`}
                      checked={item.active}
                      disabled={!canEdit || busy}
                      onChange={(e) =>
                        void submit(async () => {
                          await client.updateStockCatalogueItem(item.id, { active: e.target.checked });
                          await onChanged();
                        })
                      }
                    />
                  </td>
                  {canEdit ? (
                    <td>
                      <button
                        type="button"
                        className="link"
                        disabled={busy}
                        aria-label={`Delete ${item.name}`}
                        onClick={() =>
                          void submit(async () => {
                            await client.deleteStockCatalogueItem(item.id);
                            await onChanged();
                          })
                        }
                      >
                        Delete
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {canEdit ? (
          <form className="stacked" onSubmit={onSubmit} aria-label="Add a catalogue item">
            <div className="field">
              <label htmlFor="cat-name">New item</label>
              <input id="cat-name" required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="cat-category">Group</label>
              <select
                id="cat-category"
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value as StockGroup })}
              >
                {STOCK_GROUPS.map((g) => (
                  <option key={g} value={g}>
                    {STOCK_GROUP_TITLES[g]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="cat-unit">Unit</label>
              <input id="cat-unit" required value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="cat-code">Code / SKU</label>
              <input id="cat-code" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="cat-rule">Default quantity</label>
              <select id="cat-rule" value={draft.ruleKind} onChange={(e) => setDraft({ ...draft, ruleKind: e.target.value })}>
                <option value="">None — add by hand</option>
                {STOCK_RULE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {RULE_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>
            {draft.ruleKind ? (
              <div className="field">
                <label htmlFor="cat-rule-quantity">How many</label>
                <input
                  id="cat-rule-quantity"
                  type="number"
                  min="0"
                  step="any"
                  required
                  value={draft.ruleQuantity}
                  onChange={(e) => setDraft({ ...draft, ruleQuantity: e.target.value })}
                />
              </div>
            ) : null}
            <button type="submit" className="primary" disabled={busy}>
              Add to catalogue
            </button>
          </form>
        ) : null}
      </details>
    </section>
  );
}
